// MCP OAuth consent page (/authorize).
//
// Three phases:
//
// 1. **Resume**: if sessionStorage has `agami_oauth_jwt` +
//    `agami_oauth_request` (planted by AuthCallback after a sign-in
//    that carried oauth_continuation), POST them to
//    /oauth/authorize/complete and bounce to the returned redirect_to
//    (Claude.ai's redirect URI with ?code=… &state=…).
//
// 2. **Validate**: on a fresh request, parse the OAuth params from
//    the URL and reject anything missing or unsupported (we only
//    support response_type=code + S256 PKCE).
//
// 3. **Idle**: show the consent banner ("Allow Claude to access your
//    Agami data") + Sign in with Google / Microsoft buttons. Each
//    POSTs the continuation to /auth/{provider}/login. The backend
//    bakes the continuation into the signed state JWT it gives Google
//    / Microsoft as `state=…`; on callback the backend extracts it
//    and returns it in the JSON response, which AuthCallback (above)
//    picks up to land us back here in phase 1.

import { useEffect, useState } from "react";
import { authBase } from "./oauth-base";

type Phase = "idle" | "signing-in" | "connecting";

interface OAuthParams {
  response_type: string | null;
  client_id: string | null;
  redirect_uri: string | null;
  code_challenge: string | null;
  code_challenge_method: string | null;
  state: string | null;
  scope: string | null;
  client_name: string | null;
}

interface OAuthContinuation {
  client_id: string;
  redirect_uri: string;
  code_challenge: string;
  code_challenge_method: string;
  state: string;
  scope?: string | null;
}

export function Authorize() {
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [clientLabel, setClientLabel] = useState<string>("Claude");

  useEffect(() => {
    // Phase 1: resuming from Google/Microsoft round-trip
    const storedJwt = sessionStorage.getItem("agami_oauth_jwt");
    const storedReq = sessionStorage.getItem("agami_oauth_request");
    if (storedJwt && storedReq) {
      sessionStorage.removeItem("agami_oauth_jwt");
      sessionStorage.removeItem("agami_oauth_request");
      sessionStorage.removeItem("agami_oauth_initiated_at");

      setPhase("connecting");
      let saved: OAuthContinuation;
      try {
        saved = JSON.parse(storedReq) as OAuthContinuation;
      } catch {
        setPhase("idle");
        setError("Saved sign-in data was invalid. Please try again.");
        return;
      }

      void fetch(`${authBase()}/oauth/authorize/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jwt_token: storedJwt,
          client_id: saved.client_id,
          redirect_uri: saved.redirect_uri,
          code_challenge: saved.code_challenge,
          code_challenge_method: saved.code_challenge_method,
          state: saved.state,
          scope: saved.scope ?? null,
        }),
      })
        .then((r) => r.json())
        .then((data: { redirect_to?: string; error?: string; error_description?: string }) => {
          if (data.redirect_to) {
            window.location.href = data.redirect_to;
            return;
          }
          setPhase("idle");
          setError(
            data.error_description || data.error || "Authorization failed. Please try again.",
          );
        })
        .catch(() => {
          setPhase("idle");
          setError("Connection error. Please try again.");
        });
      return;
    }

    // Phase 2: validate URL params
    const params = readParams();
    if (
      !params.response_type ||
      !params.client_id ||
      !params.redirect_uri ||
      !params.code_challenge ||
      !params.code_challenge_method ||
      !params.state
    ) {
      setError("Invalid authorization request. Missing required parameters.");
      return;
    }
    if (params.response_type !== "code") {
      setError('Unsupported response_type. Only "code" is supported.');
      return;
    }
    if (params.code_challenge_method !== "S256") {
      setError("Unsupported PKCE method. Only S256 is supported.");
      return;
    }
    // Prefer the registered client_name forwarded by the backend (Claude,
    // ChatGPT, …). client_id is a deterministic hash (e.g.
    // "agami-95fbc24522ba108b") and carries no human label, so substring
    // sniffing on it is unreliable.
    const name = params.client_name?.trim();
    if (name) setClientLabel(name);
    else if (params.client_id?.toLowerCase().includes("claude")) setClientLabel("Claude");
    else if (params.client_id?.toLowerCase().includes("chatgpt"))
      setClientLabel("ChatGPT");
    else setClientLabel("your AI assistant");
  }, []);

  const startProvider = (provider: "google" | "microsoft") => {
    const params = readParams();
    if (!params.client_id || !params.redirect_uri || !params.state) {
      setError("Invalid authorization request.");
      return;
    }
    // Backend's OAuthContinuation model has extra="forbid" — strip
    // response_type before posting.
    const continuation = {
      client_id: params.client_id,
      redirect_uri: params.redirect_uri,
      code_challenge: params.code_challenge,
      code_challenge_method: params.code_challenge_method,
      state: params.state,
      scope: params.scope ?? null,
    };

    setError(null);
    setPhase("signing-in");

    fetch(`${authBase()}/auth/${provider}/login`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ oauth_continuation: continuation }),
    })
      .then((r) => r.json())
      .then((data: { auth_url?: string }) => {
        if (data.auth_url) {
          window.location.href = data.auth_url;
        } else {
          setPhase("idle");
          setError("Failed to start sign-in. Please try again.");
        }
      })
      .catch(() => {
        setPhase("idle");
        setError("Connection error. Please try again.");
      });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center">
          <img src="/images/logo_h.svg" alt="Agami" className="h-10 mb-8" />
        </div>

        {phase === "idle" && (
          <>
            <div className="text-center mb-6">
              <p className="text-sm text-neutral-500 mb-1">Allow</p>
              <p className="text-lg font-semibold text-neutral-900">{clientLabel}</p>
              <p className="text-sm text-neutral-500">to access your Agami data</p>
            </div>

            <div className="space-y-3">
              <ProviderButton provider="google" onClick={() => startProvider("google")} />
              <ProviderButton
                provider="microsoft"
                onClick={() => startProvider("microsoft")}
              />
            </div>
          </>
        )}

        {phase === "signing-in" && (
          <div className="flex flex-col items-center gap-2 py-10 text-neutral-600">
            <Spinner color="text-brand" />
            <span className="text-sm">Redirecting to sign-in…</span>
          </div>
        )}

        {phase === "connecting" && (
          <div className="flex flex-col items-center gap-2 py-10 text-emerald-700">
            <Spinner color="text-emerald-600" />
            <span className="text-sm">Connecting to {clientLabel}…</span>
          </div>
        )}

        {error && (
          <div className="mt-5 rounded-[12px] border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

function readParams(): OAuthParams {
  const p = new URLSearchParams(window.location.search);
  return {
    response_type: p.get("response_type"),
    client_id: p.get("client_id"),
    redirect_uri: p.get("redirect_uri"),
    code_challenge: p.get("code_challenge"),
    code_challenge_method: p.get("code_challenge_method"),
    state: p.get("state"),
    scope: p.get("scope"),
    client_name: p.get("client_name"),
  };
}

function ProviderButton({
  provider,
  onClick,
}: {
  provider: "google" | "microsoft";
  onClick: () => void;
}) {
  const label = provider === "google" ? "Continue with Google" : "Continue with Microsoft";
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full h-12 rounded-full border border-line bg-white text-neutral-800 text-[15px] font-medium flex items-center justify-center gap-3 hover:bg-chip hover:border-neutral-300 transition-colors"
    >
      {provider === "google" ? <GoogleMark /> : <MicrosoftMark />}
      {label}
    </button>
  );
}

function Spinner({ color = "text-brand" }: { color?: string }) {
  return (
    <svg className={`animate-spin h-5 w-5 ${color}`} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function GoogleMark() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.1 4 9.3 8.6 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.3 35.1 26.8 36 24 36c-5.2 0-9.6-3.3-11.2-7.9l-6.5 5C9.3 39.4 16 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2C40.9 35 44 30 44 24c0-1.3-.1-2.6-.4-3.5z" />
    </svg>
  );
}

function MicrosoftMark() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24">
      <rect x="2" y="2" width="9" height="9" fill="#F25022" />
      <rect x="13" y="2" width="9" height="9" fill="#7FBA00" />
      <rect x="2" y="13" width="9" height="9" fill="#00A4EF" />
      <rect x="13" y="13" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}
